import { useState } from 'react';

import { Button } from '@/components/design-system/Button';
import { Checkbox } from '@/components/design-system/checkbox';
import { AuthResponseBanner } from '@/components/ui/AuthResponseBanner';
import { ROUTES } from '@/consts/routes';
import type { Step } from '@/features/auth/login/types/login';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { FormInput } from '@/features/form/inputs/FormInput';
import { FormPassword } from '@/features/form/inputs/FormPassword';
import { cn } from '@/utils/cn';
import { Label } from '@radix-ui/react-label';
import { CircleCheck, CircleX, Lock, Mail } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

interface LoginFormValues {
  email: string;
  password: string;
  rememberMe?: boolean;
}

interface LoginFormProps {
  loading: boolean;
  error: AuthError | null;
  submitEmail: (email: string) => Promise<boolean>;
  submitPassword: (email: string, password: string) => Promise<void>;
}

export const LoginForm: React.FC<LoginFormProps> = ({ loading, error, submitEmail, submitPassword }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('email');

  const form = useForm<LoginFormValues>({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false
    }
  });

  const { control, handleSubmit, getValues, watch } = form;

  const emailValue = watch('email');
  const passwordValue = watch('password');

  const emailError = error?.type === 'email';
  const passwordError = error?.type === 'password';

  const onSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (step === 'email') {
      const success = await submitEmail(getValues('email'));
      if (success) {
        setStep('password');
      }
    } else {
      void handleSubmit(async (data) => {
        await submitPassword(data.email, data.password);
      })(e);
    }
  };

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      <FormInput
        control={control}
        name="email"
        leftIcon={Mail}
        rightIcon={error?.type === 'email' || step === 'password' ? (emailError ? CircleX : CircleCheck) : undefined}
        status={emailError ? 'error' : emailValue && step === 'password' ? 'success' : 'default'}
        placeholder="auth.login.emailPlaceholder"
        autoComplete={step === 'email' ? 'email' : 'new-email'}
        hideErrorMessage
      />

      {step === 'password' && (
        <div className="flex flex-col gap-2">
          <FormPassword
            control={control}
            name="password"
            leftIcon={Lock}
            status={passwordError ? 'error' : 'default'}
            placeholder="auth.login.passwordPlaceholder"
            autoComplete="new-password"
            hideErrorMessage
          />
          <div className="my-1 flex w-81 items-center justify-between">
            <div className="flex cursor-pointer items-center gap-2">
              <Controller
                control={control}
                name="rememberMe"
                render={({ field }) => (
                  <Checkbox
                    id="rememberMe"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="data-[state=checked]:bg-primary-500 data-[state=checked]:border-primary-500 hover:border-primary-500 h-4 w-4 cursor-pointer rounded-sm border border-neutral-200 transition-colors duration-200"
                  />
                )}
              />
              <Label htmlFor="rememberMe" className="cursor-pointer text-sm text-neutral-950 select-none">
                {t('auth.login.rememberMe')}
              </Label>
            </div>

            <Link
              to={ROUTES.user.passwordReset}
              className="text-primary-500 hover:text-primary-500/75 text-sm transition-all duration-300"
            >
              {t('auth.login.forgotPassword')}
            </Link>
          </div>
        </div>
      )}

      <Button
        type="submit"
        loading={loading}
        disabled={loading || (step === 'email' ? !emailValue : !passwordValue || !emailValue)}
        className={cn(
          'h-10 w-81 border text-base transition-all duration-200',
          loading && 'bg-primary-500 border-primary-500 cursor-wait text-white opacity-60',
          !loading &&
            (step === 'email'
              ? emailValue
                ? 'bg-primary-500 border-primary-500 hover:bg-primary-500/90 hover:border-primary-500/90 focus-visible:bg-primary-500 active:bg-primary-600 active:border-primary-600 text-white'
                : 'cursor-not-allowed border-neutral-200 bg-neutral-100 text-neutral-400'
              : passwordValue && emailValue
                ? 'bg-primary-500 border-primary-500 hover:bg-primary-500/90 hover:border-primary-500/90 focus-visible:bg-primary-500 active:bg-primary-600 active:border-primary-600 text-white'
                : 'cursor-not-allowed border-neutral-200 bg-neutral-100 text-neutral-400')
        )}
      >
        {step === 'email' ? t('auth.login.continue') : t('auth.login.signin')}
      </Button>

      <AuthResponseBanner error={error} />
    </form>
  );
};
